export const EXPOSURE_COUNTS_KEY = 'mercury_exposure_counts';

export type ExposureDirection = 'up' | 'down' | 'flat';

export type ExposureCount = {
  count: number;
  direction: ExposureDirection;
};

type ExposureSnapshot = {
  userId: string;
  cards: Record<string, ExposureCount>;
};

const readStorage = (): string | null => localStorage.getItem(EXPOSURE_COUNTS_KEY);

const writeStorage = (value: string) => {
  localStorage.setItem(EXPOSURE_COUNTS_KEY, value);
};

const removeStorage = () => {
  localStorage.removeItem(EXPOSURE_COUNTS_KEY);
};

/**
 * Direction of the new count. Zero new tickets show no increase.
 * A missing baseline treats a positive count as a rise.
 */
export const exposureMove = (
  previous: ExposureCount | undefined,
  findings: number
): ExposureDirection => {
  if (findings === 0) return 'flat';
  if (!previous || previous.count === 0) return 'up';
  if (findings < previous.count) return 'down';
  if (findings > previous.count) return 'up';

  return previous.direction === 'flat' ? 'up' : previous.direction;
};

export const readExposureCounts = (
  userId: string,
  read: () => string | null = readStorage
): Record<string, ExposureCount> => {
  if (!userId) return {};

  try {
    const parsed = JSON.parse(read() ?? '') as ExposureSnapshot;
    if (parsed?.userId !== userId || !parsed.cards || typeof parsed.cards !== 'object') return {};

    return parsed.cards;
  } catch {
    return {};
  }
};

/**
 * Counts to store after a load. A failed area keeps the count already stored,
 * so a zero from this load is not the next baseline.
 */
export const exposureCountsToStore = (
  previous: Record<string, ExposureCount>,
  cards: readonly {
    id: string;
    findings: number;
    direction: ExposureDirection;
    failed: boolean;
  }[]
): Record<string, ExposureCount> =>
  Object.fromEntries(
    cards.flatMap((card) => {
      if (card.failed) {
        const kept = previous[card.id];

        return kept ? [[card.id, kept]] : [];
      }

      return [[card.id, { count: card.findings, direction: card.direction }]];
    })
  );

export const writeExposureCounts = (
  userId: string,
  cards: Record<string, ExposureCount>,
  write: (value: string) => void = writeStorage
) => {
  if (!userId) return;

  const snapshot: ExposureSnapshot = { userId, cards };
  write(JSON.stringify(snapshot));
};

/** Drop the remembered counts. A new login calls this together with the visit cookie. */
export const clearExposureCounts = (remove: () => void = removeStorage) => {
  remove();
};
